import type {
  ServiceSchema,
  StockItemSchema,
  VisitServiceItemDetailSchema,
  VisitStockItemDetailSchema,
} from 'src/api';
import { KBatchDType } from 'src/api';
import {
  ColorChoices,
  CompensationCode,
  ContractInvoiceFlatRatePeriod,
  ContractInvoiceRateType,
  InsuranceTypeOptions,
  KBatchType,
  MedicalActionExclusionScope,
  MedicalActionGroupLimitScope,
  MedicalActionRestrictionPeriod,
  PatientSchema,
  PaymentModel,
  ReceiptFilterPaymentStatus,
  ReceiptInvoiceType,
  ReceiptPatientPaymentType,
  ReceiptPaymentType,
  RecurrenceIntervalTypeChoices,
  SexOptions,
  SubscriptionPaymentType,
  VisitPaymentMethod,
} from 'src/api';
import gettext from '../utils/gettext';
import type { ToggleStateFilterValue } from 'components/office/receipts/list/constants';
import type { RecurrenceOptions } from 'components/recurrence/RecurrenceForm.vue';
import { CalendarRoute } from 'src/components/calendar/constants';

const { $gettext } = gettext;

export function sexOptionsLabelMapper(sexOption: SexOptions): string {
  switch (sexOption) {
    case SexOptions.MALE:
      return $gettext('Muž');
    case SexOptions.FEMALE:
      return $gettext('Žena');
  }
}

export function packageModelOptionsMapper(tarifModel: PaymentModel): string {
  switch (tarifModel) {
    case PaymentModel.MONTHLY:
      return $gettext('Měsíční');
    case PaymentModel.YEARLY:
      return $gettext('Roční');
  }
}

export function receiptPatientPaymentTypeOptionsMapper(
  paymentType: ReceiptPatientPaymentType | ReceiptPaymentType
): string {
  switch (paymentType) {
    case ReceiptPatientPaymentType.CASH:
    case ReceiptPaymentType.CASH:
      return $gettext('Hotově');
    case ReceiptPatientPaymentType.BANK_TRANSFER:
    case ReceiptPaymentType.BANK_TRANSFER:
      return $gettext('Převodem (QR kód)');
    case ReceiptPatientPaymentType.CARD:
    case ReceiptPaymentType.CARD:
      return $gettext('Kartou');
    case ReceiptPatientPaymentType.TICKET:
    case ReceiptPaymentType.TICKET:
      return $gettext('Permanentkou');
    case ReceiptPaymentType.SUBSCRIPTION:
      return $gettext('Předplatným');
    case ReceiptPatientPaymentType.WEBPAY:
    case ReceiptPaymentType.WEBPAY:
      return $gettext('Online platba');
  }
}

export function receiptPaymentStatusOptionsMapper(
  paymentStatus: ToggleStateFilterValue
): string {
  switch (paymentStatus) {
    case 'not-issued':
      return $gettext('Nevystaveno');
    case ReceiptFilterPaymentStatus.PAID:
      return $gettext('Zaplaceno');
    case ReceiptFilterPaymentStatus.UNPAID:
      return $gettext('Nezaplaceno');
    case ReceiptFilterPaymentStatus.ALL:
      return $gettext('Status');
  }
}

export function subscriptionPaymentTypeOptionsMapper(
  subscriptionPaymentType: SubscriptionPaymentType
): string {
  switch (subscriptionPaymentType) {
    case SubscriptionPaymentType.INVOICE:
      return $gettext('Fakturace');
    case SubscriptionPaymentType.VISIT:
      return $gettext('Návštěva pacienta');
    case SubscriptionPaymentType.CANCELLED_INVOICE:
      return $gettext('Zrušená fakturace');
    case SubscriptionPaymentType.CANCELLED_VISIT:
      return $gettext('Zrušená návštěva klienta');
  }
}

export function compensationCodeMapper(
  compensationCode: CompensationCode
): string {
  switch (compensationCode) {
    case CompensationCode._3:
      return $gettext('3 - Úraz způsobený jinou osobou');
    case CompensationCode._4:
      return $gettext('4 - Alkohol a omamné látky');
    case CompensationCode._5:
      return $gettext('5 - Pracovní úraz');
    case CompensationCode._6:
      return $gettext('6 - Sociální důvody hospitalizace');
    case CompensationCode._7:
      return $gettext('7 - Porušení léčebného režimu');
    case CompensationCode._8:
      return $gettext('8 - Výměna komponent systému ASR_TM');
    case CompensationCode._9:
      return $gettext('9 - Nemoc z povolání');
  }
}

export function insuranceTypeOptionsMapper(
  insuranceTypeOptions?: InsuranceTypeOptions
): string {
  if (!insuranceTypeOptions) return '';

  switch (insuranceTypeOptions) {
    case InsuranceTypeOptions.CONTRACTUAL:
      return $gettext('Smluvní pojištění');
    case InsuranceTypeOptions.INTERNATIONAL:
      return $gettext('Pojištění EU a mezinárodní smlouvy');
    case InsuranceTypeOptions.PUBLIC:
      return $gettext('Veřejné zdravotní pojištění');
    case InsuranceTypeOptions.TRAVEL:
      return $gettext('Cestovní zdravotní pojištění');
  }
}

export function invoiceRateTypeLabelMapper(
  invoiceRateType: ContractInvoiceRateType
): string {
  switch (invoiceRateType) {
    case ContractInvoiceRateType.FLAT_RATE:
      return $gettext('Paušální');
    case ContractInvoiceRateType.ACTION:
      return $gettext('Výkonová');
    case ContractInvoiceRateType.MAX_RATE:
      return $gettext('Maximální úhrada');
  }
}

export function contractInvoiceFlatRatePeriodLabelMapper(
  contractInvoiceFlatRatePeriod: ContractInvoiceFlatRatePeriod
): string {
  switch (contractInvoiceFlatRatePeriod) {
    case ContractInvoiceFlatRatePeriod.QUARTER:
      return $gettext('Čtvrtletní');
    case ContractInvoiceFlatRatePeriod.MONTH:
      return $gettext('Měsíční');
  }
}

export function batchTypeLabelMapper(batchType: KBatchType): string {
  switch (batchType) {
    case KBatchType.CORRECTIVE:
      return $gettext('Opravná');
    case KBatchType.ORIGINAL:
      return $gettext('Původní');
  }
}

/**
 * Charakter dávky jedním písmenem do soupisu „90 P 1“ v tabulce. Plné znění
 * nese tooltip — sloupec je sloučený ze tří údajů a na slova v něm není místo.
 */
export function batchTypeShortLabelMapper(batchType: KBatchType): string {
  switch (batchType) {
    case KBatchType.CORRECTIVE:
      return $gettext('O');
    case KBatchType.ORIGINAL:
      return $gettext('P');
  }
}

export function batchDTypeLabelMapper(batchDType: KBatchDType): string {
  switch (batchDType) {
    case KBatchDType._36:
      return $gettext('DÁVKA VYÚČTOVÁNÍ CEST V NÁVŠTĚVNÍ SLUŽBĚ');
    case KBatchDType._90:
      return $gettext('DÁVKA POUKAZŮ NA VYŠETŘENÍ/OŠETŘENÍ SMÍŠENÁ');
    case KBatchDType._98:
      return $gettext('DÁVKA VYÚČTOVÁNÍ VÝKONŮ V AMBULANTNÍ PÉČI');
  }
}

export function insuranceTypeBatchLabelMapper(
  insuranceType: InsuranceTypeOptions
): string {
  switch (insuranceType) {
    case InsuranceTypeOptions.PUBLIC:
      return '1';
    case InsuranceTypeOptions.INTERNATIONAL:
      return '4';
  }
  return '';
}

/**
 * Plné znění typu připojištění. Doplňuje kód z insuranceTypeBatchLabelMapper,
 * který zná jen dva ze čtyř typů — smluvní a cestovní pojištění svůj kód
 * v číslování dávek nemají, popis ale ano.
 */
export function insuranceTypeBatchDescriptionMapper(
  insuranceType: InsuranceTypeOptions
): string {
  switch (insuranceType) {
    case InsuranceTypeOptions.PUBLIC:
      return $gettext('veřejné zdravotní pojištění');
    case InsuranceTypeOptions.CONTRACTUAL:
      return $gettext('smluvní pojištění');
    case InsuranceTypeOptions.TRAVEL:
      return $gettext('cestovní zdravotní pojištění');
    case InsuranceTypeOptions.INTERNATIONAL:
      return $gettext('pojištění EU a mezinárodní smlouvy');
  }
}

export const daysMapper = (number: number): string => {
  switch (Number(number)) {
    case 0:
      return $gettext('dnů');
    case 1:
      return $gettext('den');
    case 2:
      return $gettext('dny');
    case 3:
      return $gettext('dny');
    case 4:
      return $gettext('dny');
    default:
      return $gettext('dní');
  }
};

export const medicalActionRestrictionPeriodMapper = (
  period: MedicalActionRestrictionPeriod
): string => {
  switch (period) {
    case MedicalActionRestrictionPeriod.DAY:
      return $gettext('den');
    case MedicalActionRestrictionPeriod.WEEK:
      return $gettext('týden');
    case MedicalActionRestrictionPeriod.MONTH:
      return $gettext('měsíc');
    case MedicalActionRestrictionPeriod.QUARTER:
      return $gettext('kvartál');
    case MedicalActionRestrictionPeriod.YEAR:
      return $gettext('rok');
    case MedicalActionRestrictionPeriod.LIFETIME:
      return $gettext('život');
  }
};

export const medicalActionExclusionScopeMapper = (
  scope: MedicalActionExclusionScope
): string => {
  switch (scope) {
    case MedicalActionExclusionScope.VISIT:
      return $gettext('ve stejné návštěvě');
    case MedicalActionExclusionScope.DAY:
      return $gettext('ve stejný den');
  }
};

export const medicalActionGroupLimitScopeMapper = (
  scope: MedicalActionGroupLimitScope
): string => {
  switch (scope) {
    case MedicalActionGroupLimitScope.FT_TICKET:
      return $gettext('na žádanku');
    case MedicalActionGroupLimitScope.PATIENT_PERIOD:
      return $gettext('na pacienta');
  }
};

export const paymentMethodsMapper = (
  paymentMethod: VisitPaymentMethod
): string => {
  switch (paymentMethod) {
    case VisitPaymentMethod.CASH:
      return $gettext('Hotově');
    case VisitPaymentMethod.CARD:
      return $gettext('Kartou');
    case VisitPaymentMethod.TICKET:
      return $gettext('Permanentkou');
    case VisitPaymentMethod.SUBSCRIPTION:
      return $gettext('Předplatným');
    case VisitPaymentMethod.FT_TICKET:
      return $gettext('Pojišťovnou');
    case VisitPaymentMethod.BANK_TRANSFER:
      return $gettext('Převodem');
    case VisitPaymentMethod.FREE:
      return 'zdarma';
    case VisitPaymentMethod.WEBPAY:
      return $gettext('Online platba');
  }
};

export function receiptInvoiceTypeOptionsMapper(
  invoiceType?: ReceiptInvoiceType
): string {
  switch (invoiceType) {
    case ReceiptInvoiceType.INVOICE:
    case ReceiptInvoiceType.CORRECTIVE_TAX_DOCUMENT_INVOICE:
      return $gettext('Faktury');
    case ReceiptInvoiceType.RECEIPT:
    case ReceiptInvoiceType.CORRECTIVE_TAX_DOCUMENT_RECEIPT:
      return $gettext('Paragony');
    case undefined:
      return $gettext('Všechny doklady');
  }
}

export function recurrenceIntervalTypeChoicesMapper(
  recurrenceTypeChoices: RecurrenceOptions
): string {
  switch (recurrenceTypeChoices) {
    case RecurrenceIntervalTypeChoices.DAYS:
      return $gettext('Denně');
    case RecurrenceIntervalTypeChoices.WEEKS:
      return $gettext('Týdně');
    case RecurrenceIntervalTypeChoices.MONTHS:
      return $gettext('Měsíčně');
    default:
      return $gettext('Neopakuje se');
  }
}

export function serviceToVisitServiceItemDetailSchemaMapper(
  serviceSchema: ServiceSchema
): VisitServiceItemDetailSchema {
  return {
    price: serviceSchema.price ?? 0,
    dt_created: new Date().toISOString(),
    service_name: serviceSchema.name,
    service_id: serviceSchema.id ?? '',
    service_duration: serviceSchema.duration ?? 0,
    currency: serviceSchema.currency ?? '',
  };
}

export function stockItemToVisitStockItemDetailSchemaMapper(
  stockItemSchema: StockItemSchema
): VisitStockItemDetailSchema {
  return {
    quantity: 1,
    retail_price: stockItemSchema.retail_price_including_vat,
    dt_created: new Date().toISOString(),
    stock_item_name: stockItemSchema.name,
    stock_item_id: stockItemSchema.id ?? '',
    stock_item_external_code: stockItemSchema.external_code,
    stock_item_currency: stockItemSchema.currency ?? '',
  };
}

export function calendarNavOptionsMapper(route: CalendarRoute): string {
  switch (route) {
    case CalendarRoute.Users:
      return $gettext('Fyzioterapeuti');
    case CalendarRoute.Rooms:
      return $gettext('Místnosti');
    case CalendarRoute.Planning:
      return $gettext('Plánování');
    case CalendarRoute.RoomPlanning:
      return $gettext('Rozvrh místností');
  }
}

export function colorsDefinedByOffice(colorChoicesSchema: ColorChoices): {
  text: string;
  colorCode: string;
} {
  switch (colorChoicesSchema) {
    case ColorChoices.MODRA:
      return {
        text: $gettext('Modrá'),
        colorCode: '#1CACE4',
      };
    case ColorChoices.ZLUTA:
      return {
        text: $gettext('Žlutá'),
        colorCode: '#FFDE00',
      };
    case ColorChoices.ZELENA:
      return {
        text: $gettext('Zelená'),
        colorCode: '#B9CF38',
      };
    case ColorChoices.CERVENA:
      return {
        text: $gettext('Červená'),
        colorCode: '#E75429',
      };
  }
}

export const getLanguageOptions = (): {
  value: PatientSchema.language;
  label: string;
}[] => [
  { value: PatientSchema.language.CS, label: $gettext('Čeština') },
  { value: PatientSchema.language.SK, label: $gettext('Slovenština') },
  { value: PatientSchema.language.EN, label: $gettext('Angličtina') },
  { value: PatientSchema.language.PL, label: $gettext('Polština') },
];
